import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import AdminTrapAppStatistics from '../../components/admin/AdminTrapAppStatistics';
import { loadTrapAppMonthlyStats } from '../../lib/api/trapstats';

const calcPercent = (value, base) => {
  if (!base || base === 0) return '0.0';
  return ((value / base) * 100).toFixed(1);
};

const formatMonthStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return y + '-' + m;
};

const buildTableData = (data) => {
  if (!data) return { conversionData: [], playTypeData: [], aiPremiumData: [] };

  const { month, daysInMonth, conversion, playType, aiRecommend, premiumSubscriber } = data;
  const conversionData = [];
  const playTypeData = [];
  const aiPremiumData = [];

  for (let d = 1; d <= daysInMonth; d++) {
    const dateKey = month + '-' + String(d).padStart(2, '0');

    const mobileActive = (conversion.mobileActive && conversion.mobileActive[dateKey]) || 0;
    const routineEntry = (conversion.routineEntry && conversion.routineEntry[dateKey]) || 0;
    const challengeEntry = (conversion.challengeEntry && conversion.challengeEntry[dateKey]) || 0;
    const playActive = (conversion.playActive && conversion.playActive[dateKey]) || 0;
    const newUser = (conversion.newUser && conversion.newUser[dateKey]) || 0;

    conversionData.push({
      date: dateKey,
      mobileActive,
      routineEntry,
      routineEntryRate: calcPercent(routineEntry, mobileActive),
      challengeEntry,
      challengeEntryRate: calcPercent(challengeEntry, mobileActive),
      playActive,
      playActiveRate: calcPercent(playActive, mobileActive),
      newUser,
    });

    const aiRoutinePlay = (playType.aiRoutinePlay && playType.aiRoutinePlay[dateKey]) || 0;
    const otherPlay = (playType.otherPlay && playType.otherPlay[dateKey]) || 0;
    const challengePlay = (playType.challengePlay && playType.challengePlay[dateKey]) || 0;

    playTypeData.push({
      date: dateKey,
      aiRoutinePlay,
      otherPlay,
      challengePlay,
    });

    const newRecommend = (aiRecommend.newRecommend && aiRecommend.newRecommend[dateKey]) || 0;
    const reRecommend = (aiRecommend.reRecommend && aiRecommend.reRecommend[dateKey]) || 0;
    const android = (premiumSubscriber.android && premiumSubscriber.android[dateKey]) || 0;
    const ios = (premiumSubscriber.ios && premiumSubscriber.ios[dateKey]) || 0;

    aiPremiumData.push({
      date: dateKey,
      newRecommend,
      reRecommend,
      android,
      ios,
    });
  }

  return { conversionData, playTypeData, aiPremiumData };
};

const AdminTrapAppStatisticsContainer = () => {
  const { socketKey } = useSelector(({ socket }) => ({
    socketKey: socket.socketKey,
  }));

  const [selectedMonth, setSelectedMonth] = useState(formatMonthStr(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [monthPickerVisible, setMonthPickerVisible] = useState(false);
  const initialLoaded = useRef(false);

  const fetchMonthlyStats = useCallback(async (month) => {
    if (!socketKey) {
      setError('소켓 정보가 존재하지 않습니다.');
      return;
    }

    setLoading(true);
    setError(null);
    setTableData(null);

    try {
      const { data } = await loadTrapAppMonthlyStats({
        socketKey,
        month,
      });

      if (data && data.result !== 1) {
        throw new Error('통계 데이터를 불러오지 못했습니다.');
      }

      setTableData(buildTableData(data));
    } catch (err) {
      console.error(err);
      setError('통계 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [socketKey]);

  useEffect(() => {
    if (socketKey && !initialLoaded.current) {
      initialLoaded.current = true;
      fetchMonthlyStats(selectedMonth);
    }
  }, [socketKey, selectedMonth, fetchMonthlyStats]);

  const handlePrevMonth = useCallback(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const prev = new Date(y, m - 2, 1);
    const newMonth = formatMonthStr(prev);
    setSelectedMonth(newMonth);
    fetchMonthlyStats(newMonth);
  }, [selectedMonth, fetchMonthlyStats]);

  const handleNextMonth = useCallback(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const next = new Date(y, m, 1);
    const newMonth = formatMonthStr(next);
    setSelectedMonth(newMonth);
    fetchMonthlyStats(newMonth);
  }, [selectedMonth, fetchMonthlyStats]);

  const handleChangeMonth = useCallback((monthStr) => {
    const parts = monthStr.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const formatted = y + '-' + String(m).padStart(2, '0');
    setSelectedMonth(formatted);
    fetchMonthlyStats(formatted);
  }, [fetchMonthlyStats]);

  const handleToggleMonthPicker = useCallback(() => {
    setMonthPickerVisible((v) => !v);
  }, []);

  return (
    <AdminTrapAppStatistics
      selectedMonth={selectedMonth}
      onPrevMonth={handlePrevMonth}
      onNextMonth={handleNextMonth}
      onChangeMonth={handleChangeMonth}
      monthPickerVisible={monthPickerVisible}
      onToggleMonthPicker={handleToggleMonthPicker}
      loading={loading}
      error={error}
      tableData={tableData}
    />
  );
};

export default AdminTrapAppStatisticsContainer;
